<template>
  <!--
    Invoice view-only dialog + print choice for the invoice list rows.
    Opened through the ref: open(item, "view" | "print").
    Printing goes through the shared print module (report "invoice") with the
    built-in invoice templates: with seller, without seller, receipt printer.
  -->
  <div>
    <!-- View only -->
    <v-dialog
      v-model="viewOpen"
      :fullscreen="isMobile"
      max-width="980"
      scrollable
      content-class="idv-dialog"
    >
      <div class="idv" :class="{ 'theme--dark': $vuetify.theme.isDark }" :style="rootStyle" dir="rtl">
        <header class="idv-head">
          <span class="idv-head__icon">
            <v-icon size="20">mdi-receipt-text-outline</v-icon>
          </span>
          <div class="idv-head__titles">
            <div class="idv-head__title">
              {{ docType ? $t(docType) : $t("invoice") }}
              <span class="idv-num">{{ doc ? faNum(doc.number) : "" }}</span>
            </div>
            <div class="idv-head__sub">
              <span v-if="doc && doc.date" class="idv-num">{{ doc.date }}</span>
              <span
                v-if="status"
                class="idv-pill"
                :style="{ '--c': status.color || '#607D8B', '--cs': (status.color || '#607D8B') + '1f' }"
              >
                {{ status.name }}
              </span>
            </div>
          </div>
          <div class="idv-head__actions">
            <button type="button" class="idv-btn" :disabled="!doc" @click="choiceOpen = true">
              <v-icon size="16">mdi-printer-outline</v-icon>
              <span class="idv-btn__text">{{ $t("print") }}</span>
            </button>
            <button type="button" class="idv-btn idv-btn--icon" :title="$t('close')" @click="viewOpen = false">
              <v-icon size="18">mdi-close</v-icon>
            </button>
          </div>
        </header>

        <div class="idv-body">
          <div v-if="loading" class="idv-loading">
            <v-progress-circular indeterminate color="primary" size="28" width="3" />
          </div>
          <template v-else-if="doc">
            <section class="idv-parties">
              <div v-for="p in partyCards" :key="p.key" class="idv-party">
                <div class="idv-party__title">{{ p.title }}</div>
                <div v-for="l in p.lines" :key="l.label" class="idv-party__line">
                  <span>{{ l.label }}:</span> <b>{{ l.value }}</b>
                </div>
              </div>
            </section>

            <div class="idv-table-wrap">
              <table class="idv-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th class="idv-start">{{ $t("product") }}</th>
                    <th>{{ $t("unit") }}</th>
                    <th>{{ $t("count") }}</th>
                    <th>{{ $t("fee") }}</th>
                    <th>{{ $t("discount") }}</th>
                    <th>{{ $t("tax") }}</th>
                    <th>{{ $t("sum") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, i) in rows" :key="i">
                    <td class="idv-num">{{ faNum(i + 1) }}</td>
                    <td class="idv-start">
                      {{ productName(r) }}
                      <div v-if="r.content && r.content !== '0'" class="idv-muted">{{ r.content }}</div>
                    </td>
                    <td>{{ unitName(r) }}</td>
                    <td class="idv-num">{{ money(r.count, 3) }}</td>
                    <td class="idv-num">{{ money(r.fee) }}</td>
                    <td class="idv-num">{{ money(r.discount) }}</td>
                    <td class="idv-num">{{ money(r.tax) }}</td>
                    <td class="idv-num">{{ money(rowTotal(r)) }}</td>
                  </tr>
                  <tr v-if="!rows.length">
                    <td colspan="8" class="idv-muted">{{ $t("no-data") }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <section class="idv-foot">
              <div class="idv-note">
                <template v-if="doc.content">{{ doc.content }}</template>
              </div>
              <div class="idv-summary">
                <div
                  v-for="s in summary"
                  :key="s.label"
                  class="idv-summary__row"
                  :class="{ 'idv-summary__row--strong': s.strong }"
                >
                  <span>{{ s.label }}</span>
                  <b class="idv-num">{{ money(s.value) }}</b>
                </div>
              </div>
            </section>
          </template>
        </div>
      </div>
    </v-dialog>

    <!-- Print choice -->
    <v-dialog v-model="choiceOpen" max-width="440" content-class="idv-dialog">
      <div class="idv idv-choice" :class="{ 'theme--dark': $vuetify.theme.isDark }" :style="rootStyle" dir="rtl">
        <header class="idv-head">
          <span class="idv-head__icon">
            <v-icon size="20">mdi-printer-outline</v-icon>
          </span>
          <div class="idv-head__titles">
            <div class="idv-head__title">{{ $t("invoicePrintChoose") }}</div>
            <div class="idv-head__sub">
              <template v-if="batchItems.length">
                <span>{{ $t("invoiceGroupCount", { n: faNum(batchItems.length) }) }}</span>
                <span v-if="loading">{{ $t("invoiceGroupPrintLoading", { n: faNum(batchItems.length) }) }}</span>
              </template>
              <template v-else>
                <span>{{ docType ? $t(docType) : $t("invoice") }}</span>
                <span class="idv-num">{{ item ? faNum(item.number) : "" }}</span>
              </template>
            </div>
          </div>
          <div class="idv-head__actions">
            <button type="button" class="idv-btn idv-btn--icon" :title="$t('close')" @click="choiceOpen = false">
              <v-icon size="18">mdi-close</v-icon>
            </button>
          </div>
        </header>
        <div class="idv-options">
          <button
            v-for="o in printOptions"
            :key="o.id"
            type="button"
            class="idv-option"
            :disabled="loading && pendingTemplate === o.id"
            @click="choose(o.id)"
          >
            <span class="idv-option__icon"><v-icon size="22">{{ o.icon }}</v-icon></span>
            <span class="idv-option__text">
              <b>{{ o.title }}</b>
              <small>{{ o.hint }}</small>
            </span>
            <v-progress-circular
              v-if="loading && pendingTemplate === o.id"
              indeterminate
              size="18"
              width="2"
              color="primary"
            />
            <v-icon v-else size="18" class="idv-option__go">mdi-chevron-left</v-icon>
          </button>
        </div>
      </div>
    </v-dialog>

    <print-studio
      v-if="doc || docs.length"
      v-model="studio"
      report="invoice"
      :title="printTitle"
      :subtitle="printSubtitle"
      :columns="printColumns"
      :source="printSource"
      :built-ins="builtIns"
      :prefer-template="template"
      :extra-ctx="printCtx"
      :options="printOptionDefs"
      :share-text="shareText"
      :share-url="shareUrl"
      :batch="printBatch"
      @error="$emit('error', $event)"
    />
  </div>
</template>

<script>
import PrintStudio from "@/modules/print/PrintStudio.vue";
import { builtInInvoiceTemplates } from "@/modules/print/templates";

export default {
  name: "InvoiceDocView",
  components: { PrintStudio },
  data: () => ({
    item: null,
    doc: null,
    loading: false,
    viewOpen: false,
    choiceOpen: false,
    studio: false,
    template: "",
    pendingTemplate: "",
    // group print: selected list rows (in list order) and their loaded invoices
    batchItems: [],
    docs: [],
  }),
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.width < 800;
    },
    rootStyle() {
      var theme = this.$vuetify.theme;
      var t = theme.themes[theme.isDark ? "dark" : "light"] || {};
      var primary = typeof t.primary === "string" ? t.primary : "#1976d2";
      return { "--idv-primary": primary, "--idv-primary-soft": primary + "1a" };
    },
    docType() {
      return (this.doc && this.doc.type) || (this.item && this.item.type) || "";
    },
    status() {
      var s = (this.doc && this.doc.last_status) || (this.item && this.item.last_status);
      return s && s.status ? s.status : null;
    },
    decimal() {
      return this.decimalOf(this.doc);
    },
    rows() {
      return (this.doc && this.doc.article) || [];
    },
    summary() {
      return this.summaryOf(this.doc);
    },
    partyCards() {
      return this.partyCardsOf(this.doc);
    },
    // Group print: every invoice on its own pages, in list order.
    printBatch() {
      var app = this;
      if (!this.batchItems.length) return null;
      return this.docs.map(function (d) {
        return { rows: app.rowsOf(d), ctx: app.ctxOf(d) };
      });
    },
    printOptions() {
      return [
        { id: "inv-seller", icon: "mdi-file-document-outline", title: this.$t("invoicePrintWithSeller"), hint: this.$t("invoicePrintWithSellerHint") },
        { id: "inv-plain", icon: "mdi-file-outline", title: this.$t("invoicePrintWithoutSeller"), hint: this.$t("invoicePrintWithoutSellerHint") },
        { id: "inv-receipt", icon: "mdi-paper-roll-outline", title: this.$t("invoicePrintReceipt"), hint: this.$t("invoicePrintReceiptHint") },
      ];
    },
    printTitle() {
      return this.docType ? this.$t(this.docType) : this.$t("invoice");
    },
    printSubtitle() {
      return "";
    },
    printColumns() {
      var app = this;
      return [
        { key: "product", label: this.$t("product"), width: 3.2, value: function (r) { return app.productName(r); }, sub: function (r, o) { return app.productDetails(r, o); } },
        { key: "unit", role: "unit", label: this.$t("unit"), width: 0.9, value: function (r) { return app.unitName(r); } },
        { key: "count", role: "qty", label: this.$t("count"), format: "number", width: 0.8, value: function (r) { return app.num(r.count); } },
        { key: "fee", role: "fee", label: this.$t("fee"), format: "money", width: 1.2, value: function (r) { return app.num(r.fee); } },
        { key: "discount", label: this.$t("discount"), format: "money", width: 1.1, value: function (r) { return app.num(r.discount); } },
        { key: "tax", label: this.$t("tax"), format: "money", width: 1.1, value: function (r) { return app.num(r.tax); } },
        { key: "total", label: this.$t("sum"), format: "money", width: 1.4, value: function (r) { return app.rowTotal(r); } },
      ];
    },
    printSource() {
      var app = this;
      var all = function () {
        return app.printBatch ? [].concat.apply([], app.printBatch.map(function (b) { return b.rows; })) : app.rows;
      };
      return {
        currentRows: all,
        total: function () { return all().length; },
        fetchPage: function () {
          return Promise.resolve({ rows: all(), lastPage: 1, total: all().length });
        },
      };
    },
    builtIns() {
      return builtInInvoiceTemplates;
    },
    // Options of the previous invoice print, saved with the template.
    printOptionDefs() {
      return [
        { key: "currency", label: this.$t("printOptCurrency"), default: true },
        { key: "productCode", label: this.$t("showProductCode") },
        { key: "productTaxCode", label: this.$t("showProductTaxCode") },
        { key: "productSerial", label: this.$t("showProductSerial") },
        { key: "articleContent", label: this.$t("showArticleContent") },
        { key: "entrepot", label: this.$t("showEntrepotArticle") },
        { key: "remain", label: this.$t("showRemain") },
      ];
    },
    shareText() {
      if (this.batchItems.length) return this.$t("invoiceGroupCount", { n: this.faNum(this.docs.length) });
      var d = this.doc || {};
      var due = this.summary.find(function (x) { return x.strong; });
      var total = due ? due.value : 0;
      var cur = d.currency && d.currency.name ? " " + d.currency.name : "";
      return [
        this.printTitle + " " + this.faNum(d.number || ""),
        d.date ? this.$t("date") + ": " + this.faNum(d.date) : "",
        this.$t("payable") + ": " + this.money(total) + cur,
      ].filter(Boolean).join(" | ");
    },
    // Public invoice link (same as the old print page's "copy link").
    shareUrl() {
      return this.item && !this.batchItems.length ? "https://abr.abriron.com/print/invoice/" + this.item.uid : "";
    },
    printCtx() {
      return this.ctxOf(this.doc || this.docs[0]);
    },
  },
  watch: {
    // Closing the studio (or the choice) after a print-only open leaves nothing behind.
    studio(v) {
      if (!v) this.template = "";
    },
    // Closing the choice while the invoice still loads cancels the print.
    choiceOpen(v) {
      if (!v) this.pendingTemplate = "";
    },
  },
  methods: {
    // Entry point from the list: mode "view" (view only) or "print" (choice first).
    open(item, mode) {
      if (!item) return;
      this.batchItems = [];
      this.docs = [];
      var same = this.item && this.doc && this.item.uid === item.uid;
      this.item = item;
      if (!same) this.doc = null;
      if (mode === "print") this.choiceOpen = true;
      else this.viewOpen = true;
      if (!same) this.load();
    },
    load() {
      var app = this;
      var uid = app.item.uid;
      app.loading = true;
      return app.$axios
        .get(app.$endpoint + "invoice/" + uid + "/edit", { params: { print: "active" } })
        .then(function (res) {
          if (!app.item || app.item.uid !== uid) return;
          app.doc = res.data;
        })
        .catch(function (err) {
          app.$emit("error", err);
          app.choiceOpen = false;
          app.viewOpen = false;
        })
        .finally(function () {
          app.loading = false;
          if (app.pendingTemplate && app.doc) app.startPrint(app.pendingTemplate);
        });
    },
    // Group print from the list: choice dialog, then all invoices (list order).
    openBatch(items) {
      var app = this;
      if (!items || !items.length) return;
      if (items.length === 1) return app.open(items[0], "print");
      app.item = null;
      app.doc = null;
      app.batchItems = items.slice();
      app.docs = [];
      app.choiceOpen = true;
      app.loading = true;
      var token = (app.batchToken = {});
      var out = new Array(items.length);
      var next = 0;
      // a few requests at a time so a big selection doesn't flood the server
      var worker = function () {
        if (next >= items.length) return Promise.resolve();
        var i = next++;
        return app.$axios
          .get(app.$endpoint + "invoice/" + items[i].uid + "/edit", { params: { print: "active" } })
          .then(function (res) {
            out[i] = res.data;
          })
          .then(worker);
      };
      Promise.all([worker(), worker(), worker(), worker()])
        .then(function () {
          if (app.batchToken !== token) return;
          app.docs = out.filter(Boolean);
        })
        .catch(function (err) {
          if (app.batchToken !== token) return;
          app.$emit("error", err);
          app.choiceOpen = false;
        })
        .finally(function () {
          if (app.batchToken !== token) return;
          app.loading = false;
          if (app.pendingTemplate && app.docs.length) app.startPrint(app.pendingTemplate);
        });
    },
    choose(id) {
      this.pendingTemplate = id;
      var ready = this.batchItems.length ? this.docs.length : this.doc;
      if (ready && !this.loading) this.startPrint(id);
    },
    startPrint(id) {
      this.template = id;
      this.choiceOpen = false;
      this.pendingTemplate = "";
      this.$nextTick(() => {
        this.studio = true;
      });
    },
    decimalOf(doc) {
      return doc && doc.currency ? Number(doc.currency.decimal) || 0 : 0;
    },
    // Rows carry their invoice so detail lines (warehouse …) find it in a group print.
    rowsOf(doc) {
      return ((doc && doc.article) || []).map(function (r) {
        return Object.assign({}, r, { __doc: doc });
      });
    },
    summaryOf(doc) {
      var app = this;
      var t = ((doc && doc.article) || []).reduce(
        function (acc, r) {
          acc.gross += app.num(r.fee) * app.num(r.count) * app.rate(r);
          acc.tax += app.num(r.tax);
          acc.extra += app.num(r.construction_wages) + app.num(r.profit) + app.num(r.commission);
          return acc;
        },
        { gross: 0, tax: 0, extra: 0 }
      );
      var discount = this.num(doc && doc.discount);
      var list = [{ label: this.$t("sumItem"), value: t.gross }];
      if (discount) list.push({ label: this.$t("discount"), value: discount });
      if (t.tax) list.push({ label: this.$t("tax"), value: t.tax });
      if (t.extra) list.push({ label: this.$t("invoiceExtraCosts"), value: t.extra });
      list.push({ label: this.$t("payable"), value: t.gross - discount + t.tax + t.extra, strong: true });
      var c = doc && doc.customer;
      if (c && c.remain) list.push({ label: this.$t("remainAmountCustomer"), value: this.num(c.remain.remain), option: "remain" });
      return list;
    },
    partyCardsOf(doc) {
      var s = (doc && doc.self) || {};
      var c = (doc && doc.customer) || {};
      var seller = [
        { label: this.$t("name"), value: s.abrName },
        { label: this.$t("national-code"), value: s.nationalCode },
        { label: this.$t("financial-code"), value: s.financialCode },
        { label: this.$t("phone"), value: s.phone || s.mobile },
        { label: this.$t("address"), value: s.address },
      ].filter(this.filled);
      var buyer = [
        { label: this.$t("name"), value: c.name },
        { label: this.$t("national-code"), value: c.national_code },
        { label: this.$t("financial-code"), value: c.financial_code },
        { label: this.$t("phone"), value: c.mobile || c.phone },
        { label: this.$t("address"), value: c.address },
      ].filter(this.filled);
      // Purchases swap the roles: the account is the seller.
      var buy = /buy/i.test((doc && doc.type) || "");
      var me = { key: "me", title: this.$t(buy ? "buyer" : "seller"), lines: seller };
      var other = { key: "other", title: this.$t(buy ? "seller" : "buyer"), lines: buyer };
      return [me, other].filter(function (p) { return p.lines.length; });
    },
    ctxOf(doc) {
      var cards = this.partyCardsOf(doc);
      var me = cards.find(function (p) { return p.key === "me"; });
      var other = cards.find(function (p) { return p.key === "other"; });
      var self = (doc && doc.self) || {};
      var dec = this.decimalOf(doc);
      var ctx = {
        // "seller" in the template = our own company box, "buyer" = the account.
        parties: { seller: me || null, buyer: other || null },
        summary: this.summaryOf(doc).map(function (s) {
          return { label: s.label, value: Number(s.value.toFixed(dec)), strong: s.strong, option: s.option };
        }),
        note: (doc && doc.content) || "",
        currency: doc && doc.currency ? doc.currency.name : "",
        // Invoice number / date in the header meta box (Persian digits by the template).
        info: [
          { label: this.$t("number"), value: doc && doc.number },
          { label: this.$t("date"), value: doc && doc.date },
        ],
        itemsLabel: this.$t("invoicePrintItems"),
        signatureLabels: [this.$t("sellerSignature"), this.$t("buyerSignature")],
      };
      if (self.abrName) ctx.company = self.abrName;
      if (self.logo && self.logo.link) ctx.logo = self.logo.link;
      return ctx;
    },
    num(v) {
      var n = parseFloat(v);
      return isNaN(n) ? 0 : n;
    },
    rate(r) {
      var n = parseFloat(r.cutieRate);
      return isNaN(n) || !n ? 1 : n;
    },
    rowTotal(r) {
      return this.num(r.fee) * this.num(r.count) * this.rate(r) + this.num(r.tax) - this.num(r.discount) +
        this.num(r.construction_wages) + this.num(r.profit) + this.num(r.commission);
    },
    // Extra lines under the product name, per template options.
    productDetails(r, o) {
      var p = r.product || {};
      var out = [];
      if (o.productCode && p.code != null && p.code !== "") out.push(this.$t("product-code") + ": " + p.code);
      if (o.productTaxCode && p.taxid != null && p.taxid !== "") out.push(this.$t("product-tax-code") + ": " + p.taxid);
      if (o.productSerial) {
        var sn = (p.serials || []).map(function (x) { return String((x && (x.serial || x.value)) || "").trim(); }).filter(Boolean);
        if (sn.length) out.push(this.$t("serial-numbers") + ": " + sn.join("، "));
      }
      if (o.entrepot) {
        var d = r.__doc || this.doc;
        var e = r.article_entrepot || (d && d.entrepot && d.entrepot.entrepot);
        if (e && e.name) out.push(this.$t("entrepot") + ": " + e.name);
      }
      if (o.articleContent && r.content && r.content !== "0") out.push(r.content);
      return out;
    },
    productName(r) {
      return r.product && r.product.name ? String(r.product.name).replace(/\s{2,}/g, " ").trim() : "";
    },
    unitName(r) {
      return r.unit ? r.unit.fame || r.unit.name || "" : "";
    },
    filled(l) {
      return l.value != null && l.value !== "" && l.value !== "null";
    },
    money(v, decimals) {
      var n = Number(v) || 0;
      var d = decimals == null ? this.decimal : decimals;
      var s = this.$numberFormat ? this.$numberFormat(Number(n.toFixed(d))) : n.toFixed(d);
      return this.faNum(s);
    },
    faNum(v) {
      var s = String(v == null ? "" : v);
      return String(this.$i18n && this.$i18n.locale).indexOf("fa") === 0
        ? s.replace(/[0-9]/g, function (d) { return "۰۱۲۳۴۵۶۷۸۹"[d]; })
        : s;
    },
  },
};
</script>

<style scoped>
.idv {
  --idv-line: rgba(15, 23, 42, 0.08);
  --idv-bg: #f3f5f9;
  --idv-surface: #fff;
  --idv-muted: rgba(15, 23, 42, 0.6);
  display: flex;
  flex-direction: column;
  max-height: 100%;
  background: var(--idv-bg);
  border-radius: 6px;
  overflow: hidden;
  color: #1e293b;
}
.idv.theme--dark {
  --idv-line: rgba(255, 255, 255, 0.1);
  --idv-bg: #121212;
  --idv-surface: #1e1e1e;
  --idv-muted: rgba(255, 255, 255, 0.6);
  color: #e2e8f0;
}
::v-deep .idv-dialog {
  border-radius: 6px;
}
.idv-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--idv-surface);
  border-bottom: 1px solid var(--idv-line);
}
.idv-head__icon {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--idv-primary-soft);
  flex-shrink: 0;
}
.idv-head__icon .v-icon {
  color: var(--idv-primary);
}
.idv-head__titles {
  flex: 1 1 auto;
  min-width: 0;
}
.idv-head__title {
  font-weight: 700;
  font-size: 1rem;
}
.idv-head__sub {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: var(--idv-muted);
  margin-top: 2px;
}
.idv-head__actions {
  display: flex;
  gap: 6px;
}
.idv-btn {
  height: 34px;
  padding: 0 12px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 0, 0, 0.05);
  color: inherit;
  font-size: 0.85rem;
}
.idv.theme--dark .idv-btn {
  background: rgba(255, 255, 255, 0.08);
}
.idv-btn .v-icon {
  color: inherit;
}
.idv-btn:disabled {
  opacity: 0.45;
}
.idv-btn--icon {
  width: 34px;
  padding: 0;
  justify-content: center;
}
.idv-pill {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 6px;
  color: var(--c);
  background: var(--cs);
  font-weight: 600;
}
.idv-num {
  direction: ltr;
  unicode-bidi: plaintext;
  font-variant-numeric: tabular-nums;
}
.idv-body {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
}
.idv-body > * {
  flex-shrink: 0;
}
.idv-loading {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}
.idv-parties {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}
.idv-party {
  background: var(--idv-surface);
  border: 1px solid var(--idv-line);
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 0.82rem;
  line-height: 1.8;
}
.idv-party__title {
  font-weight: 700;
  color: var(--idv-primary);
}
.idv-party__line span {
  color: var(--idv-muted);
}
.idv-table-wrap {
  background: var(--idv-surface);
  border: 1px solid var(--idv-line);
  border-radius: 6px;
  overflow-x: auto;
}
.idv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}
.idv-table th,
.idv-table td {
  padding: 8px 10px;
  text-align: center;
  border-bottom: 1px solid var(--idv-line);
  white-space: nowrap;
}
.idv-table th {
  background: var(--idv-primary-soft);
  font-weight: 700;
}
.idv-table td.idv-start,
.idv-table th.idv-start {
  text-align: start;
  white-space: normal;
  min-width: 180px;
}
.idv-table tbody tr:last-child td {
  border-bottom: 0;
}
.idv-muted {
  color: var(--idv-muted);
  font-size: 0.75rem;
}
.idv-foot {
  display: flex;
  gap: 12px;
  margin-top: 12px;
  align-items: flex-start;
}
.idv-note {
  flex: 1 1 auto;
  white-space: pre-wrap;
  font-size: 0.82rem;
  color: var(--idv-muted);
}
.idv-summary {
  flex: 0 0 300px;
  background: var(--idv-surface);
  border: 1px solid var(--idv-line);
  border-radius: 6px;
  overflow: hidden;
}
.idv-summary__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--idv-line);
  font-size: 0.84rem;
}
.idv-summary__row:last-child {
  border-bottom: 0;
}
.idv-summary__row--strong {
  font-weight: 800;
  background: var(--idv-primary-soft);
}
.idv-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
}
.idv-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 6px;
  border: 1px solid var(--idv-line);
  background: var(--idv-surface);
  color: inherit;
  text-align: start;
  transition: border-color 0.15s, background 0.15s;
}
.idv-option:hover:not(:disabled) {
  border-color: var(--idv-primary);
  background: var(--idv-primary-soft);
}
.idv-option__icon {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--idv-primary-soft);
  flex-shrink: 0;
}
.idv-option__icon .v-icon {
  color: var(--idv-primary);
}
.idv-option__text {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.idv-option__text small {
  color: var(--idv-muted);
  font-size: 0.75rem;
}
.idv-option__go {
  color: var(--idv-muted) !important;
}
@media (max-width: 799px) {
  .idv-foot {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .idv-summary {
    flex-basis: auto;
  }
  .idv-btn__text {
    display: none;
  }
  .idv-btn {
    width: 34px;
    padding: 0;
    justify-content: center;
  }
}
</style>
